import { useEffect, useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import {
  TbArrowDownLeft,
  TbArrowUpRight,
  TbArrowsExchange,
  TbChartBar,
  TbCoin,
  TbQrcode,
} from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useColorMode } from "../../../../../Hooks/useColorMode";
import { fetchBaasPixMetrics, fetchBaasPeerPixMetrics } from "../../../../../Services/baasApiApi";
import { fetchBaasCrossborderHistory } from "../../../../../Services/baasCrossborderApi";
import { fetchBaasStablesTransactions } from "../../../../../Services/baasStablesApi";
import { formatMonetaryValue } from "../../../../../Utils/formatters";
import { BAAS_STABLES_METRICS_PATH } from "../../constants";
import { shouldShowBaasCustodyAggregate } from "../../baasInstanceListFilter";
import {
  getDefaultProcessingDateRange,
  getProcessingDateQuery,
  getProcessingPeriodQuery,
  getProcessingVolumeLabelKey,
} from "../baasHomeProcessingDateFilters";
import {
  aggregateEfxSummary,
  aggregateStablesSummary,
  buildPixDailySeries,
  formatPeriodLabel,
} from "../baasHomeProductSummaryUtils";
import { BaasHomeProcessingDateFilter } from "./BaasHomeProcessingDateFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const GOLD = "#e7a73f";
const EMERALD = "#10b981";
const SKY = "#0ea5e9";
const TEAL = "#14b8a6";
const ROSE = "#f43f5e";
const SLATE = "#64748b";
const ORANGE = "#f97316";
const LIME = "#84cc16";
const VIOLET = "#8b5cf6";

const STABLE_TOKEN_COLORS = {
  BRL1: GOLD,
  USDC: SKY,
  USDT: TEAL,
  EURC: VIOLET,
  BRZ: ORANGE,
};

const STABLE_TOKEN_FALLBACK_COLORS = [ROSE, SLATE, LIME, EMERALD, ORANGE];

const colorForStableToken = (asset, index = 0) =>
  STABLE_TOKEN_COLORS[String(asset || "").toUpperCase()] ||
  STABLE_TOKEN_FALLBACK_COLORS[index % STABLE_TOKEN_FALLBACK_COLORS.length];

const DEFAULT_STABLE_TOKEN_ROWS = ["BRL1", "USDC", "USDT", "EURC"].map((asset) => ({
  asset,
  volume: 0,
}));

const EMPTY_PIX = {
  volume: 0,
  txCount: 0,
  successRate: 100,
  sentVolume: 0,
  receivedVolume: 0,
  trendVolume: [],
  volumeOverTime: { labels: [], values: [] },
  displayCurrency: "BRL",
};

const EMPTY_STABLES = {
  assets: [],
  tokens: [],
  volumeByAsset: {},
  volumeUsdc: 0,
  volumeUsdt: 0,
  volumeBrl1: 0,
  txCount: 0,
  successRate: 100,
  dailySeries: { labels: [], byAsset: {}, primary: [], secondary: [] },
};

const EMPTY_EFX = {
  volume: 0,
  inbound: 0,
  outbound: 0,
  txCount: 0,
  successRate: 100,
  primaryPair: "USD/BRL",
  displayCurrency: "BRL",
  dailySeries: { labels: [], primary: [], secondary: [] },
};

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatPercent = (value) =>
  `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value)}%`;

const buildBarOptions = (isDark, formatValue) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        label: (context) => formatValue(context.parsed.x || 0),
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        color: isDark ? "rgba(255,255,255,0.06)" : "rgba(229,231,235,0.7)",
        drawBorder: false,
      },
      ticks: {
        color: isDark ? "#9ca3af" : "#9ca3af",
        font: { size: 10 },
        callback: (value) => {
          if (value >= 1000) return `${Math.round(value / 1000)}k`;
          return value;
        },
      },
      border: { display: false },
    },
    y: {
      grid: { display: false },
      ticks: {
        color: isDark ? "#d1d5db" : "#6b7280",
        font: { size: 11, weight: "600" },
      },
      border: { display: false },
    },
  },
});

const buildLineOptions = (isDark, formatValue) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      align: "start",
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        pointStyle: "circle",
        padding: 12,
        color: isDark ? "#9ca3af" : "#6b7280",
        font: { size: 10 },
      },
    },
    tooltip: {
      backgroundColor: isDark ? "#333" : "#242424",
      titleFont: { size: 11, weight: "600" },
      bodyFont: { size: 11 },
      padding: 8,
      cornerRadius: 6,
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || "";
          return `${label}: ${formatValue(context.parsed.y || 0)}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: isDark ? "rgba(255,255,255,0.06)" : "rgba(229,231,235,0.55)",
        drawBorder: false,
      },
      ticks: {
        color: isDark ? "#6b7280" : "#9ca3af",
        font: { size: 10 },
        maxRotation: 0,
        maxTicksLimit: 8,
      },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      display: false,
      grid: { display: false },
      border: { display: false },
    },
  },
  elements: {
    line: { tension: 0.35, borderWidth: 2 },
    point: { radius: 0, hoverRadius: 4, hitRadius: 10 },
  },
});

const ChartPanel = ({ title, subtitle, children, className = "" }) => (
  <div
    className={`summary-card flex min-h-[220px] flex-col overflow-hidden ${className}`.trim()}
  >
    <div className="mb-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="m-0 text-sm font-bold text-brand-dark dark:text-gray-100">{title}</p>
        {subtitle ? (
          <p className="mt-0.5 m-0 text-[11px] text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </div>
      <div className="summary-card-icon shrink-0">
        <TbChartBar className="h-4 w-4 text-brand-gold-mid" aria-hidden />
      </div>
    </div>
    <div className="min-h-0 flex-1">{children}</div>
  </div>
);

const MetricChip = ({ icon: Icon, label, value, tone = "out", color }) => {
  const toneClass =
    tone === "in"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "sky"
        ? "text-sky-600 dark:text-sky-400"
        : "text-[#9a7340] dark:text-brand-gold-mid";

  const bgClass =
    tone === "in"
      ? "bg-emerald-500/10"
      : tone === "sky"
        ? "bg-sky-500/10"
        : "bg-brand-gold/15";

  const hasCustomColor = Boolean(color);

  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/80 px-2.5 py-2 dark:border-brand-dark-border dark:bg-brand-dark-surface/80">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          hasCustomColor ? "" : bgClass
        }`}
        style={hasCustomColor ? { backgroundColor: `${color}22` } : undefined}
      >
        <Icon
          className={`h-3.5 w-3.5 ${hasCustomColor ? "" : toneClass}`}
          style={hasCustomColor ? { color } : undefined}
          aria-hidden
        />
      </div>
      <div className="min-w-0">
        <p className="m-0 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p
          className={`m-0 truncate text-sm font-bold tabular-nums ${hasCustomColor ? "" : toneClass}`}
          style={hasCustomColor ? { color } : undefined}
        >
          {value}
        </p>
      </div>
    </div>
  );
};

const DualSeriesCharts = ({
  flowTitle,
  flowSubtitle,
  dailyTitle,
  dailySubtitle,
  leftLabel,
  rightLabel,
  leftValue,
  rightValue,
  leftTone = "in",
  rightTone = "out",
  leftIcon = TbArrowDownLeft,
  rightIcon = TbArrowUpRight,
  leftColor = EMERALD,
  rightColor = GOLD,
  dailySeries,
  formatValue,
  loading = false,
}) => {
  const { isDark } = useColorMode();
  const barOptions = useMemo(
    () => buildBarOptions(isDark, formatValue),
    [isDark, formatValue],
  );
  const lineOptions = useMemo(
    () => buildLineOptions(isDark, formatValue),
    [isDark, formatValue],
  );

  const barData = useMemo(
    () => ({
      labels: [leftLabel, rightLabel],
      datasets: [
        {
          data: [leftValue || 0, rightValue || 0],
          backgroundColor: [leftColor, rightColor],
          borderRadius: 6,
          barThickness: 22,
          maxBarThickness: 28,
        },
      ],
    }),
    [leftLabel, rightLabel, leftValue, rightValue, leftColor, rightColor],
  );

  const lineData = useMemo(() => {
    const labels = dailySeries?.labels?.length ? dailySeries.labels : ["—"];
    const primary = dailySeries?.primary?.length ? dailySeries.primary : [0];
    const secondary = dailySeries?.secondary?.length ? dailySeries.secondary : [0];

    return {
      labels,
      datasets: [
        {
          label: leftLabel,
          data: primary,
          borderColor: leftColor,
          backgroundColor: `${leftColor}1f`,
          fill: true,
          pointBackgroundColor: leftColor,
        },
        {
          label: rightLabel,
          data: secondary,
          borderColor: rightColor,
          backgroundColor: `${rightColor}1f`,
          fill: true,
          pointBackgroundColor: rightColor,
        },
      ],
    };
  }, [dailySeries, leftLabel, rightLabel, leftColor, rightColor]);

  if (loading) {
    return (
      <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="summary-card h-[220px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
        <div className="summary-card h-[220px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
      </div>
    );
  }

  return (
    <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
      <ChartPanel title={flowTitle} subtitle={flowSubtitle}>
        <div className="mb-3 grid grid-cols-2 gap-2">
          <MetricChip
            icon={leftIcon}
            label={leftLabel}
            value={formatValue(leftValue)}
            tone={leftTone}
          />
          <MetricChip
            icon={rightIcon}
            label={rightLabel}
            value={formatValue(rightValue)}
            tone={rightTone}
          />
        </div>
        <div className="h-[110px] w-full">
          <Bar data={barData} options={barOptions} />
        </div>
      </ChartPanel>

      <ChartPanel title={dailyTitle} subtitle={dailySubtitle}>
        <div className="h-[168px] w-full">
          <Line data={lineData} options={lineOptions} />
        </div>
      </ChartPanel>
    </div>
  );
};

/** Stables summary: horizontal bars + multi-series daily curve for every token present. */
const MultiAssetCharts = ({
  flowTitle,
  flowSubtitle,
  dailyTitle,
  dailySubtitle,
  tokens = [],
  dailySeries,
  formatTokenValue,
  loading = false,
}) => {
  const { isDark } = useColorMode();

  const tokenRows = useMemo(() => {
    if (tokens?.length) return tokens;
    return DEFAULT_STABLE_TOKEN_ROWS;
  }, [tokens]);

  const formatValue = useMemo(
    () => (value, asset = "USDC") => formatTokenValue(value, asset),
    [formatTokenValue],
  );

  const barOptions = useMemo(
    () =>
      buildBarOptions(isDark, (value) => formatValue(value, tokenRows[0]?.asset || "USDC")),
    [isDark, formatValue, tokenRows],
  );

  const lineOptions = useMemo(() => {
    const base = buildLineOptions(isDark, (value) => formatValue(value, "USDC"));
    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            label: (context) => {
              const asset = context.dataset.label || "USDC";
              return `${asset}: ${formatValue(context.parsed.y || 0, asset)}`;
            },
          },
        },
      },
    };
  }, [isDark, formatValue]);

  const barData = useMemo(
    () => ({
      labels: tokenRows.map((row) => row.asset),
      datasets: [
        {
          data: tokenRows.map((row) => Number(row.volume || 0)),
          backgroundColor: tokenRows.map((row, index) => colorForStableToken(row.asset, index)),
          borderRadius: 6,
          barThickness: tokenRows.length > 4 ? 16 : 22,
          maxBarThickness: tokenRows.length > 4 ? 20 : 28,
        },
      ],
    }),
    [tokenRows],
  );

  const lineData = useMemo(() => {
    const labels = dailySeries?.labels?.length ? dailySeries.labels : ["—"];
    const byAsset = dailySeries?.byAsset || {};

    return {
      labels,
      datasets: tokenRows.map((row, index) => {
        const color = colorForStableToken(row.asset, index);
        const series = byAsset[row.asset];
        return {
          label: row.asset,
          data: Array.isArray(series) && series.length ? series : labels.map(() => 0),
          borderColor: color,
          backgroundColor: `${color}1f`,
          fill: true,
          pointBackgroundColor: color,
        };
      }),
    };
  }, [dailySeries, tokenRows]);

  const chipCols =
    tokenRows.length <= 2
      ? "grid-cols-2"
      : tokenRows.length === 3
        ? "grid-cols-3"
        : "grid-cols-2 sm:grid-cols-4";

  const barHeight = Math.max(110, Math.min(200, 28 + tokenRows.length * 28));

  if (loading) {
    return (
      <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="summary-card h-[240px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
        <div className="summary-card h-[240px] animate-pulse bg-gray-50 dark:bg-brand-dark-elevated" />
      </div>
    );
  }

  return (
    <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
      <ChartPanel title={flowTitle} subtitle={flowSubtitle}>
        <div className={`mb-3 grid gap-2 ${chipCols}`}>
          {tokenRows.map((row, index) => (
            <MetricChip
              key={row.asset}
              icon={TbCoin}
              label={row.asset}
              value={formatValue(row.volume, row.asset)}
              color={colorForStableToken(row.asset, index)}
            />
          ))}
        </div>
        <div className="w-full" style={{ height: barHeight }}>
          <Bar
            data={barData}
            options={{
              ...barOptions,
              plugins: {
                ...barOptions.plugins,
                tooltip: {
                  ...barOptions.plugins.tooltip,
                  callbacks: {
                    label: (context) => {
                      const asset = context.label || tokenRows[context.dataIndex]?.asset || "USDC";
                      return formatValue(context.parsed.x || 0, asset);
                    },
                  },
                },
              },
            }}
          />
        </div>
      </ChartPanel>

      <ChartPanel title={dailyTitle} subtitle={dailySubtitle}>
        <div className="h-[200px] w-full">
          <Line data={lineData} options={lineOptions} />
        </div>
      </ChartPanel>
    </div>
  );
};

const ProductSummarySection = ({
  icon: Icon,
  title,
  viewTo,
  viewLabel,
  disabled = false,
  disabledMessage,
  meta,
  children,
}) => (
  <section className="min-w-0 w-full space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <div className={`summary-card-icon ${disabled ? "opacity-60" : ""}`}>
          <Icon
            className={`h-4 w-4 ${disabled ? "text-gray-400" : "text-[#e7a73f]"}`}
            aria-hidden
          />
        </div>
        <div className="min-w-0">
          <h2
            className={`dashboard-section-label m-0 ${
              disabled ? "text-gray-400" : ""
            }`}
          >
            {title}
          </h2>
          {meta && !disabled ? (
            <p className="m-0 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{meta}</p>
          ) : null}
        </div>
      </div>
      {!disabled && viewTo ? (
        <Link
          to={viewTo}
          className="link-no-zoom inline-flex items-center gap-1 text-xs font-semibold text-[#e7a73f] transition hover:underline dark:text-brand-gold-mid"
        >
          {viewLabel}
        </Link>
      ) : null}
    </div>

    {disabled ? (
      <div className="app-card flex min-h-[12rem] items-center justify-center border border-dashed border-gray-200 bg-gray-50/80 p-6 text-center opacity-80 dark:border-brand-dark-border dark:bg-brand-dark-surface/60">
        <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">{disabledMessage}</p>
      </div>
    ) : (
      <div className="space-y-3">{children}</div>
    )}
  </section>
);

const collectSameEnvInstances = (selectedInstance, instances = []) => {
  const env = selectedInstance?.environment;
  const seen = new Set();
  const rows = [];
  (Array.isArray(instances) && instances.length ? instances : [selectedInstance])
    .filter(Boolean)
    .forEach((row) => {
      if (env && row.environment && row.environment !== env) return;
      const key = String(row.baasInstanceId ?? row.instanceId ?? row.id ?? "");
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    });
  return rows.length ? rows : selectedInstance ? [selectedInstance] : [];
};

export const BaasHomeProductSummaries = ({ displayCurrency = "BRL" }) => {
  const { selectedInstance, dataEpoch, listInstanceFilter, instances } = useBaasInstance();
  const instanceKey = selectedInstance?.id ?? null;
  const aggregateAll = shouldShowBaasCustodyAggregate(
    selectedInstance,
    listInstanceFilter,
  );
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const [processingDateRange, setProcessingDateRange] = useState(getDefaultProcessingDateRange);
  const [loading, setLoading] = useState(false);
  const [pixMetrics, setPixMetrics] = useState(EMPTY_PIX);
  const [stablesMetrics, setStablesMetrics] = useState(EMPTY_STABLES);
  const [efxMetrics, setEfxMetrics] = useState(EMPTY_EFX);

  const period = useMemo(
    () => getProcessingPeriodQuery(processingDateRange),
    [processingDateRange],
  );

  const dateQuery = useMemo(
    () => getProcessingDateQuery(processingDateRange),
    [processingDateRange],
  );

  const periodLabel = useMemo(
    () => formatPeriodLabel(processingDateRange),
    [processingDateRange],
  );

  const volumeLabel = useMemo(
    () => intl.get(getProcessingVolumeLabelKey(processingDateRange)),
    [processingDateRange],
  );

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !instanceKey) {
      setPixMetrics(EMPTY_PIX);
      setStablesMetrics(EMPTY_STABLES);
      setEfxMetrics(EMPTY_EFX);
      return undefined;
    }

    setPixMetrics(EMPTY_PIX);
    setStablesMetrics(EMPTY_STABLES);
    setEfxMetrics(EMPTY_EFX);
    setLoading(true);
    (async () => {
      try {
        const [pix, stablesTx, efxHistory] = await Promise.all([
          (aggregateAll
            ? fetchBaasPeerPixMetrics(selectedInstance, { period, ...dateQuery })
            : fetchBaasPixMetrics(selectedInstance, { period, ...dateQuery })
          ).catch(() => null),
          (aggregateAll
            ? Promise.all(
                collectSameEnvInstances(selectedInstance, instances).map((row) =>
                  fetchBaasStablesTransactions(row).catch(() => []),
                ),
              ).then((batches) => batches.flat())
            : fetchBaasStablesTransactions(selectedInstance)
          ).catch(() => []),
          fetchBaasCrossborderHistory(selectedInstance, {
            aggregatePeers: aggregateAll,
            startDate: processingDateRange?.dateFrom,
            endDate: processingDateRange?.dateTo,
          }).catch(() => []),
        ]);
        if (cancelled) return;

        if (pix) {
          setPixMetrics({
            volume: pix.volume,
            txCount: pix.txCount,
            successRate: pix.successRate,
            sentVolume: pix.sentVolume,
            receivedVolume: pix.receivedVolume,
            trendVolume: pix.trendVolume,
            volumeOverTime: pix.volumeOverTime,
            displayCurrency: pix.currency || "BRL",
          });
        } else {
          setPixMetrics(EMPTY_PIX);
        }

        setStablesMetrics(aggregateStablesSummary(stablesTx, processingDateRange));
        setEfxMetrics(aggregateEfxSummary(efxHistory, processingDateRange));
      } catch {
        if (!cancelled) {
          setPixMetrics(EMPTY_PIX);
          setStablesMetrics(EMPTY_STABLES);
          setEfxMetrics(EMPTY_EFX);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, instanceKey, dataEpoch, period, dateQuery, processingDateRange, aggregateAll, instances]);

  const pixDailySeries = useMemo(
    () => buildPixDailySeries(pixMetrics, processingDateRange),
    [pixMetrics, processingDateRange],
  );

  const formatBrl = useMemo(
    () => (value) =>
      formatMonetaryValue(value, pixMetrics.displayCurrency || "BRL", {
        maximumFractionDigits: 0,
      }),
    [pixMetrics.displayCurrency],
  );

  const formatStableToken = useMemo(
    () => (value, asset = "USDC") =>
      formatMonetaryValue(value, asset || "USDC", { maximumFractionDigits: 0 }),
    [],
  );

  const stablesTokens = useMemo(() => {
    if (Array.isArray(stablesMetrics.tokens) && stablesMetrics.tokens.length) {
      return stablesMetrics.tokens;
    }
    const assets = Array.isArray(stablesMetrics.assets) ? stablesMetrics.assets : [];
    const volumeByAsset = stablesMetrics.volumeByAsset || {};
    return assets.map((asset) => ({
      asset,
      volume: Number(volumeByAsset[asset] || 0),
    }));
  }, [stablesMetrics]);

  const formatEfx = useMemo(
    () => (value) =>
      formatMonetaryValue(value, efxMetrics.displayCurrency || displayCurrency || "BRL", {
        maximumFractionDigits: 0,
      }),
    [efxMetrics.displayCurrency, displayCurrency],
  );

  const pixAllowed = !capabilitiesLoading && can("processing.pix.view");
  const stablesAllowed = !capabilitiesLoading && can("processing.stables.view");
  const efxAllowed = !capabilitiesLoading && can("processing.efx.view");
  const hasAnyRail = pixAllowed || stablesAllowed || efxAllowed;

  // Permissions drive visibility — no permitted rail → hide the whole block.
  if (!capabilitiesLoading && !hasAnyRail) {
    return null;
  }

  return (
    <div className="min-w-0 w-full space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="dashboard-section-label m-0">
            {intl.get("baas.home.dashboard.productSummaries.section")}
          </p>
          <p className="m-0 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {volumeLabel}
            {periodLabel ? ` · ${periodLabel}` : ""}
          </p>
        </div>
        <BaasHomeProcessingDateFilter
          value={processingDateRange}
          onChange={setProcessingDateRange}
        />
      </div>

      {pixAllowed ? (
        <ProductSummarySection
          icon={TbQrcode}
          title={intl.get("baas.home.dashboard.productSummaries.pixTitle")}
          viewTo="/baas/contas"
          viewLabel={intl.get("baas.home.dashboard.productSummaries.viewPix")}
          meta={intl.get("baas.home.dashboard.processingRails.txCount", {
            count: formatCount(pixMetrics.txCount),
          }) +
            " · " +
            intl.get("baas.home.dashboard.processingRails.successRate", {
              rate: formatPercent(pixMetrics.successRate),
            })}
        >
          <DualSeriesCharts
            flowTitle={intl.get("baas.home.dashboard.productSummaries.pixFlowTitle")}
            flowSubtitle={periodLabel || volumeLabel}
            dailyTitle={intl.get("baas.home.dashboard.productSummaries.pixDailyTitle")}
            dailySubtitle={intl.get("baas.home.dashboard.productSummaries.pixDailySubtitle")}
            leftLabel={intl.get("baas.home.dashboard.productSummaries.pixIn")}
            rightLabel={intl.get("baas.home.dashboard.productSummaries.pixOut")}
            leftValue={pixMetrics.receivedVolume}
            rightValue={pixMetrics.sentVolume}
            dailySeries={pixDailySeries}
            formatValue={formatBrl}
            loading={loading}
          />
        </ProductSummarySection>
      ) : null}

      {stablesAllowed ? (
        <ProductSummarySection
          icon={TbCoin}
          title={intl.get("baas.home.dashboard.productSummaries.stablesTitle")}
          viewTo={BAAS_STABLES_METRICS_PATH}
          viewLabel={intl.get("baas.home.dashboard.productSummaries.viewStables")}
          meta={intl.get("baas.home.dashboard.processingRails.txCount", {
            count: formatCount(stablesMetrics.txCount),
          }) +
            " · " +
            intl.get("baas.home.dashboard.processingRails.successRate", {
              rate: formatPercent(stablesMetrics.successRate),
            })}
        >
          <MultiAssetCharts
            flowTitle={intl.get("baas.home.dashboard.productSummaries.stablesFlowTitle")}
            flowSubtitle={intl.get("baas.home.dashboard.productSummaries.stablesFlowSubtitle")}
            dailyTitle={intl.get("baas.home.dashboard.productSummaries.stablesDailyTitle")}
            dailySubtitle={intl.get("baas.home.dashboard.productSummaries.stablesDailySubtitle")}
            tokens={stablesTokens}
            dailySeries={stablesMetrics.dailySeries}
            formatTokenValue={formatStableToken}
            loading={loading}
          />
        </ProductSummarySection>
      ) : null}

      {efxAllowed ? (
        <ProductSummarySection
          icon={TbArrowsExchange}
          title={intl.get("baas.home.dashboard.productSummaries.efxTitle")}
          viewTo="/conta-internacional"
          viewLabel={intl.get("baas.home.dashboard.productSummaries.viewEfx")}
          meta={
            intl.get("baas.home.dashboard.processingRails.txCount", {
              count: formatCount(efxMetrics.txCount),
            }) +
            " · " +
            intl.get("baas.home.dashboard.processingRails.successRate", {
              rate: formatPercent(efxMetrics.successRate),
            }) +
            " · " +
            intl.get("baas.home.dashboard.processingRails.pair", {
              pair: efxMetrics.primaryPair,
            })
          }
        >
          <DualSeriesCharts
            flowTitle={intl.get("baas.home.dashboard.productSummaries.efxFlowTitle")}
            flowSubtitle={intl.get("baas.home.dashboard.productSummaries.efxFlowSubtitle")}
            dailyTitle={intl.get("baas.home.dashboard.productSummaries.efxDailyTitle")}
            dailySubtitle={intl.get("baas.home.dashboard.productSummaries.efxDailySubtitle")}
            leftLabel={intl.get("baas.home.dashboard.productSummaries.efxIn")}
            rightLabel={intl.get("baas.home.dashboard.productSummaries.efxOut")}
            leftValue={efxMetrics.inbound}
            rightValue={efxMetrics.outbound}
            leftIcon={TbArrowDownLeft}
            rightIcon={TbArrowUpRight}
            dailySeries={efxMetrics.dailySeries}
            formatValue={formatEfx}
            loading={loading}
          />
        </ProductSummarySection>
      ) : null}
    </div>
  );
};

export default BaasHomeProductSummaries;
